import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface TeamSubscription {
  team_id: string;
  team_name: string;
  team_slug: string;
  subscription_status: string;
  seat_count: number;
  max_seats: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_ends_at: string | null;
  canceled_at: string | null;
  member_count: number;
  user_role: string;
}

function isWithinGracePeriod(canceledAt: string | null): boolean {
  if (!canceledAt) return false;
  const cancelDate = new Date(canceledAt);
  const graceEnd = new Date(cancelDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  return new Date() < graceEnd;
}

export function useTeamSubscription() {
  const { user } = useAuth();
  const [teamSubscription, setTeamSubscription] = useState<TeamSubscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setTeamSubscription(null);
      setLoading(false);
      return;
    }

    async function fetchTeamSubscription() {
      try {
        const { data, error } = await supabase.rpc("get_team_subscription_for_user", {
          _user_id: user.id,
        });

        if (error) throw error;

        // RPC returns an array; we take the first (or only) row
        const row = Array.isArray(data) ? data[0] : data;
        setTeamSubscription(row || null);
      } catch {
        // If the RPC doesn't exist yet (migration not applied), silently fail
        setTeamSubscription(null);
      } finally {
        setLoading(false);
      }
    }

    fetchTeamSubscription();

    // Subscribe to teams table changes for realtime updates
    const channel = supabase
      .channel("team-subscription-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "teams",
        },
        () => {
          // Refetch on any teams table update — filter is applied by the RPC
          fetchTeamSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const isTeamPro =
    teamSubscription?.subscription_status === "active" ||
    teamSubscription?.subscription_status === "trialing" ||
    (teamSubscription?.subscription_status === "canceled" &&
      isWithinGracePeriod(teamSubscription.canceled_at));

  const isTeamAdmin =
    teamSubscription?.user_role === "owner" ||
    teamSubscription?.user_role === "admin";

  return {
    teamSubscription,
    isTeamPro: !!isTeamPro,
    isTeamAdmin,
    loading,
  };
}
